import type { FilterOption } from '@/components/FilterMenu'
import { OrgFilterTagValue } from '@/components/OrgFilterMenu/OrgFilterTagValue'
import type { DeploymentLabelOption } from '@/contexts/useApiaries'
import { NBSP } from '@/utils'
import { Box, Button, Flex, HStack, Icon, Tag, Text } from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface ActiveFiltersProps {
  selectedDeploymentLabel: DeploymentLabelOption | null
  handleDeploymentLabelChange: (option: DeploymentLabelOption | null) => void

  selectedTemplate: FilterOption | null
  handleTemplateChange: (option: FilterOption | null) => void

  selectedApprovalState: FilterOption | null
  handleApprovalStateChange: (option: FilterOption | null) => void

  selectedHiveState: FilterOption | null
  handleHiveStateChange: (option: FilterOption | null) => void

  selectedHiveId: string | null
  handleHiveIdSearch: (value: string) => void

  selectedTags: FilterOption[]
  setSelectedTags: (tags: FilterOption[]) => void

  selectedOrgId: string | null
  handleOrgIdSearch: (value: string) => void

  setPage: (pageNumber: number | ((prev: number) => number)) => void
  clearAllFilters: () => void
}

const filterOptionColorScheme = 'gray'

export function ActiveFilters({
  selectedDeploymentLabel,
  handleDeploymentLabelChange,
  selectedTemplate,
  handleTemplateChange,
  selectedApprovalState,
  handleApprovalStateChange,
  selectedHiveState,
  handleHiveStateChange,
  selectedHiveId,
  handleHiveIdSearch,
  selectedTags,
  setSelectedTags,
  selectedOrgId,
  handleOrgIdSearch,
  setPage,
  clearAllFilters,
}: ActiveFiltersProps) {
  const { t } = useTranslation()

  return (
    <HStack
      width="100%"
      justifyContent="space-between"
      data-testid="active-filters"
      columnGap={0.5}
    >
      <Flex flexWrap="wrap" gap={2}>
        {selectedDeploymentLabel && (
          <Tag.Root
            size="md"
            borderRadius="full"
            variant="solid"
            colorPalette={filterOptionColorScheme}
          >
            <HStack>
              <Text>
                {t('clusterConfigTable.filters.deploymentLabel')}:{NBSP}
                {selectedDeploymentLabel.label}
              </Text>
              <Icon
                as={Plus}
                boxSize={4}
                ml={1}
                transform="rotate(45deg)"
                cursor="pointer"
                onClick={() => {
                  setPage(1)
                  handleDeploymentLabelChange(null)
                }}
                _hover={{ opacity: 0.8 }}
              />
            </HStack>
          </Tag.Root>
        )}

        {selectedTemplate && (
          <Tag.Root
            size="md"
            borderRadius="full"
            variant="solid"
            colorPalette={filterOptionColorScheme}
          >
            <HStack>
              <Text>
                {t('clusterConfigTable.filters.template')}:{NBSP}
                {selectedTemplate.value}
              </Text>
              <Icon
                as={Plus}
                boxSize={4}
                ml={1}
                transform="rotate(45deg)"
                cursor="pointer"
                onClick={() => {
                  setPage(1)
                  handleTemplateChange(null)
                }}
                _hover={{ opacity: 0.8 }}
              />
            </HStack>
          </Tag.Root>
        )}

        {selectedApprovalState && (
          <Tag.Root
            size="md"
            borderRadius="full"
            variant="solid"
            colorPalette={filterOptionColorScheme}
          >
            <HStack>
              <Text>
                {t('clusterConfigTable.filters.approvalState')}:{NBSP}
                {selectedApprovalState.label}
              </Text>
              <Icon
                as={Plus}
                boxSize={4}
                ml={1}
                transform="rotate(45deg)"
                cursor="pointer"
                onClick={() => {
                  setPage(1)
                  handleApprovalStateChange(null)
                }}
                _hover={{ opacity: 0.8 }}
              />
            </HStack>
          </Tag.Root>
        )}

        {selectedHiveState && (
          <Tag.Root
            size="md"
            borderRadius="full"
            variant="solid"
            colorPalette={filterOptionColorScheme}
          >
            <HStack>
              <Text>
                {t('clusterConfigTable.filters.hiveState')}:{NBSP}
                {selectedHiveState.label}
              </Text>
              <Icon
                as={Plus}
                boxSize={4}
                ml={1}
                transform="rotate(45deg)"
                cursor="pointer"
                onClick={() => {
                  setPage(1)
                  handleHiveStateChange(null)
                }}
                _hover={{ opacity: 0.8 }}
              />
            </HStack>
          </Tag.Root>
        )}

        {selectedHiveId && (
          <Tag.Root
            size="md"
            borderRadius="full"
            variant="solid"
            colorPalette={filterOptionColorScheme}
          >
            <HStack>
              <Text>
                {t('clusterConfigTable.filters.hiveId')}: {selectedHiveId}
              </Text>
              <Icon
                as={Plus}
                boxSize={4}
                ml={1}
                transform="rotate(45deg)"
                cursor="pointer"
                onClick={() => {
                  setPage(1)
                  handleHiveIdSearch('')
                }}
                _hover={{ opacity: 0.8 }}
              />
            </HStack>
          </Tag.Root>
        )}

        {selectedTags.length > 0 &&
          selectedTags.map((tag) => (
            <Tag.Root
              key={tag.value}
              size="md"
              borderRadius="full"
              variant="solid"
              colorPalette={filterOptionColorScheme}
            >
              <HStack>
                <Text>
                  {t('clusterConfigTable.filters.tags')}: {tag.label}
                </Text>
                <Icon
                  as={Plus}
                  boxSize={4}
                  ml={1}
                  transform="rotate(45deg)"
                  cursor="pointer"
                  onClick={() => {
                    const newTags = selectedTags.filter(
                      (t) => t.value !== tag.value,
                    )
                    setSelectedTags(newTags)
                    setPage(1)
                  }}
                  _hover={{ opacity: 0.8 }}
                />
              </HStack>
            </Tag.Root>
          ))}

        {selectedOrgId && (
          <Tag.Root
            size="md"
            borderRadius="full"
            variant="solid"
            colorPalette={filterOptionColorScheme}
          >
            <HStack>
              <Text>
                {t('clusterConfigTable.filters.organization')}:{NBSP}
                <OrgFilterTagValue orgId={selectedOrgId} />
              </Text>
              <Icon
                data-testid="close-org-id-tag"
                as={Plus}
                boxSize={4}
                ml={1}
                transform="rotate(45deg)"
                cursor="pointer"
                onClick={() => {
                  setPage(1)
                  handleOrgIdSearch('')
                }}
                _hover={{ opacity: 0.8 }}
              />
            </HStack>
          </Tag.Root>
        )}
      </Flex>

      <Box alignSelf="flex-end">
        <Button variant="ghost" size="sm" onClick={clearAllFilters}>
          {t('components.filterMenu.clearAllFilters')}
        </Button>
      </Box>
    </HStack>
  )
}
